import {
  Component, Inject, OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl, FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA, MatDialogModule, MatDialogRef
} from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ToastrService } from 'ngx-toastr';
import { switchMap } from 'rxjs';
import { AppState } from '~core/states/app/app.state';
import { STILUserDataService } from '~shared/services/apiSTILUserController';
import { MatIconModule } from '@angular/material/icon';


export const RESET_TITLE = 'Reset Password';
export const VALIDATION_TITLE = 'Invalid Password';

@Component({
  selector: 'app-password-reset-dialog',
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule
  ],
  templateUrl: './password-reset-dialog.component.html',
  styleUrls: [ './password-reset-dialog.component.scss' ]
})
export class PasswordResetDialogComponent implements OnInit {
  passwordForm: UntypedFormGroup;

  showCurrentPassword = false;

  showPassword = false;

  showPasswordVerify = false;

  passwordControlHasValue: boolean;

  resetMode = true;

  title = RESET_TITLE;

  get passwordCtrl(): AbstractControl {
    return this.passwordForm.get('password');
  }

  get passwordVerifyCtrl(): AbstractControl {
    return this.passwordForm.get('passwordVerify');
  }

  get currentPassword(): AbstractControl {
    return this.passwordForm.get('currentPassword');
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {externalPasswordValidation: string},
    private userSvc: STILUserDataService,
    private appState: AppState,
    private matDialogRef: MatDialogRef<PasswordResetDialogComponent>,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.passwordForm = new UntypedFormGroup({
      currentPassword: new UntypedFormControl('', Validators.required),
      password: new UntypedFormControl('', [ Validators.required, passwordPattenValidator() ]),
      passwordVerify: new UntypedFormControl('')
    }, passwordsMatchValidator());

    if (this.data?.externalPasswordValidation) {
      this.resetMode = false;
      this.title = VALIDATION_TITLE;
      this.passwordCtrl.setValue(this.data.externalPasswordValidation);
    }
  }

  getInputType(show: boolean): string {
    return show ? 'text' : 'password';
  }

  resetPassword(): void {
    const facilityId = this.appState.get('facilityId');
    const user = this.appState.get('user');
    this.userSvc.isValidPassword(user.ID, this.passwordForm.controls['currentPassword'].value).pipe(
      switchMap(() => this.userSvc.resetPassword(this.appState.get('user')?.ID, this.passwordCtrl.value) )
    )
      .subscribe(
        res => {
          this.toastr.success('Password successfully reset');
          this.matDialogRef.close();
        },
        err => this.toastr.error('Password reset failed. Confirm current Password.')
      );
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowPasswordVerify(): void {
    this.showPasswordVerify = !this.showPasswordVerify;
  }

  toggleShowCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

}

export function passwordPattenValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }

    const hasLength = (value.length > 7) && (value.length < 36);
    const hasUpperCase = /[A-Z]+/.test(value);
    const hasLowerCase = /[a-z]+/.test(value);
    const hasNumeric = /[0-9]+/.test(value);
    const hasSpecialChar = /[^A-Za-z 0-9]/.test(value);
    const patternsValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar && hasLength;

    const errors = {
      caseInvalid: !hasLowerCase || !hasUpperCase,
      numericInvalid: !hasNumeric,
      specialCharInvalid: !hasSpecialChar,
      lengthInvalid: !hasLength
    };

    return !patternsValid ? { patternsInvalid: errors } : null;
  };
}

export function passwordsMatchValidator(): ValidatorFn {
  return (group: UntypedFormGroup): ValidationErrors => {
    const password = group.controls['password'].value;
    const passwordVerify = group.controls['passwordVerify'].value;
    const invalid = password !== passwordVerify;

    return invalid ? { noMatch: true } : null;
  };
}
